import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Icon,
  Spinner,
} from '@nike/eds';
import { Suspense, useRef, useState, Fragment } from 'react';
import { useRevalidator } from 'react-router-dom';
import classnames from 'classnames';
import { useInfiniteQuery } from '@tanstack/react-query';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';
import { QUERY_KEYS } from '../../constants';
import '@alenaksu/json-viewer';
import { JsonViewerElement } from 'types/jsonviewer';

function ChildrenConceptView({
  parentId,
  className,
}: {
  parentId: string;
  className?: string;
}) {
  const revalidator = useRevalidator();
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.RECOMMEND_CONCEPT_LIST, parentId],
    queryFn: async ({ pageParam }) => {
      if (pageParam) {
        return RecommendConceptsClient.next(pageParam);
      } else {
        return RecommendConceptsClient.getConceptsByWhereCause(
          `parentId==${parentId}`,
        );
      }
    },
    getNextPageParam: (lastPage, pages) => lastPage.pages.next,
    staleTime: Infinity,
    suspense: true,
    onError: (err: keyable) => err.status === 401 && revalidator.revalidate(),
  });

  const cx = 'tw-ml-5' + (className ? ' ' + className : '');

  return (
    <ul>
      {data &&
        data.pages.map((page) => (
          <Fragment
            key={page.pages.next ? page.pages.next : `${parentId}_page_1`}
          >
            {page.objects.map((rw, idx) => {
              const key = `${parentId}_${rw.id}`;
              return (
                <li key={key} className={cx}>
                  <ConceptView
                    id={rw.id}
                    data={rw}
                    loadChildren={false}
                    key={rw.id}
                  />
                </li>
              );
            })}
          </Fragment>
        ))}
      {hasNextPage ? (
        <li className='tw-ml-14 tw-pl-6'>
          <Button
            variant='ghost'
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            <span className='eds-color--grey-1 tw-italic'>Load more</span>
          </Button>
          {isFetchingNextPage ? <Spinner className='tw-ml-12 tw-my-2' /> : null}
        </li>
      ) : null}
    </ul>
  );
}

export default function ConceptView({
  id,
  data,
  loadChildren,
  className,
}: {
  id: string;
  data: RecommendConcept;
  loadChildren: boolean;
  className?: string;
}) {
  const ref = useRef<JsonViewerElement>(null);
  const [showJsonView, setShowJsonView] = useState(false);
  const [loadChildConcepts, setLoadChildConcepts] = useState(loadChildren);

  const cx = 'tw-px-3' + (className ? ' ' + className : '');
  const jsonViewCx = classnames('tw-ml-4', { 'tw-hidden': !showJsonView });
  const iconName = loadChildConcepts ? 'CaretDown' : 'CaretRight';
  return (
    <div className={cx}>
      <div>
        <div className='tw-inline-block'>
          <IconButton
            icon={iconName}
            label=''
            variant='ghost'
            onClick={() => setLoadChildConcepts(!loadChildConcepts)}
          />
        </div>
        <div className='tw-inline-block'>
          <ToggleButtonGroup size='small'>
            <ToggleButton
              onClick={() => setShowJsonView(!showJsonView)}
              active={showJsonView}
              beforeSlot={<Icon name='Expand' />}
              title='Details'
            >
              {data.name}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>
      <div className={jsonViewCx}>
        <ButtonGroup>
          <Button
            variant='secondary'
            size='small'
            disableRipple
            onClick={() => ref.current && ref.current.expandAll()}
          >
            Expand All
          </Button>
          <Button
            variant='secondary'
            size='small'
            disableRipple
            onClick={() => ref.current && ref.current.collapseAll()}
          >
            Collapse All
          </Button>
        </ButtonGroup>
        <div className='tw-px-3' style={{ backgroundColor: '#2a2f3a' }}>
          <json-viewer id={id} ref={ref}>
            {JSON.stringify(data)}
          </json-viewer>
        </div>
      </div>
      {loadChildConcepts && (
        <Suspense fallback={<Spinner className='tw-ml-12 tw-my-2' />}>
          <ChildrenConceptView parentId={data.id} />
        </Suspense>
      )}
    </div>
  );
}

ConceptView.propTypes = {
  id: PropTypes.string,
  data: PropTypes.object,
  loadChildren: PropTypes.bool,
  classNames: PropTypes.string,
};
